import React, { useState } from "react"
import useTitle from "@/hooks/useTitle"
import { addIcon } from "@/assets/img/imgAssets"
import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline"
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
  UserGroupIcon,
  BanknotesIcon,
} from "@heroicons/react/24/solid"
import PulseLoader from "react-spinners/PulseLoader"
import {
  useGetAccountsQuery,
  useCreateNewAccountMutation,
} from "./accountsApiSlice"
import { useGetContactOrganizationsQuery } from "../contactOrganizations/contactOrganizationsApiSlice"

const EtAccountList = ({
  setActiveAdminView,
  onAdd,
  onUpdate,
  onDelete,
  userOrgId = "",
}) => {
  useTitle("Accounts List")

  const [showDetails, setShowDetails] = useState({})
  const [autoCreateMessage, setAutoCreateMessage] = useState("")

  const {
    data: accountsData,
    isError,
    error,
    isLoading,
  } = useGetAccountsQuery({ ownerOrgId: userOrgId })

  const { contactOrganizations } = useGetContactOrganizationsQuery(
    "contactOrganizationsList",
    {
      selectFromResult: ({ data }) => ({
        contactOrganizations: data?.ids?.map(id => data?.entities[id]) || [],
      }),
    }
  )

  const [
    createNewAccount,
    { isLoading: isCreatingAccount },
  ] = useCreateNewAccountMutation()

  const handleToggleDetails = (accountId) => {
    setShowDetails(prev => ({
      ...prev,
      [accountId]: !prev[accountId],
    }))
  }

  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  if (isLoading) return <PulseLoader color={"#ea580c"} />

  if (isError) {
    return (
      <div className="text-sm text-orange-700">
        Error occurred: {error?.data?.message}
      </div>
    )
  }

  const accounts =
    accountsData?.ids?.map(id => {
      const account = accountsData?.entities[id]

      return {
        _id: account?._id,
        accountName: account?.accountName,
        accountDesc: account?.accountDesc,
        accountIntro: account?.accountIntro,
        accountOutro: account?.accountOutro,
        accountStatus: account?.accountStatus,
        accountStage: account?.accountStage,
        accountRevenueSize: account?.accountRevenueSize,
        accountProfitability: account?.accountProfitability,
        accountStartDate: account?.accountStartDate,
        contactOrganizationId: account?.contactOrganizationId,
        availableToOrganizationBUs: account?.availableToOrganizationBUs || [],
        accountOwners: account?.accountOwners || [],
        accountTeamMembers: account?.accountTeamMembers || [],
        accountGrowthPriorities: account?.accountGrowthPriorities || [],
        accountRisks: account?.accountRisks || [],
        active: account?.active,
      }
    }) || []

  const existingAccountContactOrgIds = accounts
    ?.map(account =>
      account?.contactOrganizationId?._id ||
      account?.contactOrganizationId?.id ||
      account?.contactOrganizationId
    )
    ?.filter(Boolean)

  const clientContactOrganizations = contactOrganizations?.filter(org => {
    const typeName =
      org?.contactOrganizationTypeId?.contactOrganizationTypeName ||
      org?.contactOrganizationTypeId?.name ||
      ""

    return typeName?.toLowerCase() === "client org"
  })

  const missingClientContactOrganizations = clientContactOrganizations?.filter(org => {
    const contactOrgId = org?._id || org?.id

    return !existingAccountContactOrgIds?.includes(contactOrgId)
  })

  const handleAutoCreateAccounts = async () => {
    if (!userOrgId) {
      setAutoCreateMessage("User organization is missing. Cannot create accounts.")
      return
    }

    if (!missingClientContactOrganizations?.length) {
      setAutoCreateMessage("No missing client accounts found.")
      return
    }

    try {
      for (const contactOrg of missingClientContactOrganizations) {
        await createNewAccount({
          ownerOrgId: userOrgId,
          contactOrganizationId: contactOrg?._id || contactOrg?.id,
          accountName: contactOrg?.contactOrganizationName,
          accountDesc: contactOrg?.contactOrganizationDesc || "",
          accountStatus: "Prospect",
          availableToOrganizationBUs: [],
        }).unwrap()
      }

      setAutoCreateMessage(
        `${missingClientContactOrganizations.length} account(s) created from Client Org contact organizations.`
      )
    } catch (err) {
      console.error(err)
      setAutoCreateMessage(err?.data?.message || "Error creating accounts.")
    }
  }

  return (
    <div className="bg-gray-200 m-1 p-1 rounded font-poppins mx-auto">
      <div className="flex justify-between items-center m-1 p-1">
        <div className="flex justify-start items-center">
          <p className="text-2xl m-1 p-1 font-bold">Accounts</p>

          <img
            src={addIcon}
            alt=""
            className="h-10 w-10 cursor-pointer"
            onClick={onAdd}
          />
        </div>

        <div>
          <button
            type="button"
            onClick={handleAutoCreateAccounts}
            disabled={isCreatingAccount}
            className="bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded px-3 py-2 disabled:bg-gray-400"
          >
            {isCreatingAccount ? "Creating..." : "Auto Create Accounts"}
          </button>
        </div>
      </div>

      {autoCreateMessage && (
        <div className="bg-white m-1 p-2 rounded">
          <p className="text-xs text-orange-700">{autoCreateMessage}</p>
        </div>
      )}

      {accounts?.length === 0 && (
        <div className="bg-white rounded m-1 p-4">
          <p className="text-sm text-gray-600">
            No Accounts found. Click Auto Create Accounts to create them from
            Client Org contact organizations.
          </p>
        </div>
      )}

      {accounts?.map((item, index) => (
        <div key={item._id} className="m-1 p-1 bg-white rounded">
          <div className="m-1 p-1 bg-gray-100 rounded">
            <div className="flex justify-between items-center">
              <div className="flex justify-start items-center mx-2">
                <div
                  onClick={() => handleToggleDetails(item._id)}
                  className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-white"
                >
                  {showDetails[item._id] ? (
                    <MinusIcon className="h-6 w-6 text-orange-600" />
                  ) : (
                    <PlusIcon className="h-4 w-4 text-orange-600" />
                  )}
                </div>

                <div className="m-1 p-1">
                  <div className="flex justify-start items-center flex-wrap">
                    <p className="font-bold">
                      {index + 1} - {item.accountName}
                    </p>

                    {item.accountStatus && (
                      <p className="text-[10px] bg-orange-100 text-orange-700 rounded-full mx-2 px-2 py-1">
                        {item.accountStatus}
                      </p>
                    )}

                    {item.accountStage && (
                      <p className="text-[10px] bg-gray-200 text-gray-700 rounded-full mx-1 px-2 py-1">
                        {item.accountStage}
                      </p>
                    )}

                    {!item.active && (
                      <p className="text-[10px] bg-red-100 text-red-700 rounded-full mx-2 px-2 py-1">
                        Inactive
                      </p>
                    )}
                  </div>

                  <p className="text-xs">{item.accountDesc}</p>

                  {item?.contactOrganizationId?.contactOrganizationName && (
                    <p className="text-[10px] text-gray-500 mt-1">
                      Client Org: {item.contactOrganizationId.contactOrganizationName}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-start items-center mx-2">
                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <PencilSquareIcon
                    onClick={() => onUpdate(item?._id)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>

                <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-white">
                  <TrashIcon
                    onClick={() => onDelete(item?._id)}
                    className="h-4 w-4 text-orange-600 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {showDetails[item._id] && (
              <div className="m-1 p-1 bg-gray-200 rounded">
                <div className="m-1 p-1 rounded bg-white">
                  {item?.accountIntro && (
                    <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                      <p
                        className="m-1 p-1"
                        dangerouslySetInnerHTML={createMarkup(item.accountIntro)}
                      ></p>
                    </div>
                  )}

                  <div className="flex justify-start items-center my-2">
                    <BuildingOffice2Icon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      Client Organization:{" "}
                      <span className="font-normal text-gray-500">
                        {item?.contactOrganizationId?.contactOrganizationName ||
                          "Not linked"}
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-start items-center my-2">
                    <CalendarDaysIcon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      Account Start Date:{" "}
                      <span className="font-normal text-gray-500">
                        {item?.accountStartDate
                          ? formatDate(item.accountStartDate)
                          : "Not defined"}
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-start items-center my-2">
                    <BanknotesIcon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      Revenue Size:{" "}
                      <span className="font-normal text-gray-500">
                        {item?.accountRevenueSize || "Not defined"}
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-start items-center my-2">
                    <BanknotesIcon className="h-4 w-4 text-orange-600 mx-2" />
                    <p className="text-xs font-bold">
                      Profitability:{" "}
                      <span className="font-normal text-gray-500">
                        {item?.accountProfitability || "Not defined"}
                      </span>
                    </p>
                  </div>

                  {item?.availableToOrganizationBUs?.length > 0 && (
                    <div className="m-1 p-1">
                      <p className="text-xs font-bold m-1 p-1">Linked BUs</p>

                      {item.availableToOrganizationBUs.map((buItem, buIndex) => (
                        <div
                          key={buIndex}
                          className="rounded m-1 p-1 bg-orange-50 inline-block"
                        >
                          <p className="text-[10px] mx-1 px-1">
                            {buItem?.organizationBUId?.organizationBUName || "BU"}
                            {buItem?.organizationBUId?.organizationBUCode
                              ? ` (${buItem.organizationBUId.organizationBUCode})`
                              : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item?.accountOwners?.length > 0 && (
                    <div className="m-1 p-1">
                      <div className="flex justify-start items-center">
                        <UserGroupIcon className="h-4 w-4 text-orange-600 mx-1" />
                        <p className="text-xs font-bold m-1 p-1">
                          Account Owners
                        </p>
                      </div>

                      {item.accountOwners.map((owner, ownerIndex) => (
                        <div
                          key={ownerIndex}
                          className="rounded m-1 p-1 bg-gray-200 inline-block"
                        >
                          <p className="text-[10px] mx-1 px-1">
                            {owner?.ownerId?.name ||
                              owner?.ownerId?.username ||
                              "Owner"}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item?.accountTeamMembers?.length > 0 && (
                    <div className="m-1 p-1">
                      <div className="flex justify-start items-center">
                        <UserGroupIcon className="h-4 w-4 text-orange-600 mx-1" />
                        <p className="text-xs font-bold m-1 p-1">
                          Account Team
                        </p>
                      </div>

                      {item.accountTeamMembers.map((member, memberIndex) => (
                        <div
                          key={memberIndex}
                          className="rounded m-1 p-1 bg-gray-200"
                        >
                          <p className="text-[10px] mx-1 px-1">
                            {member?.teamMemberId?.name ||
                              member?.teamMemberId?.username ||
                              "Team Member"}
                            {member?.teamMemberRole
                              ? ` - ${member.teamMemberRole}`
                              : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {item?.accountGrowthPriorities?.length > 0 && (
                    <div className="m-1 p-1">
                      <p className="text-xs font-bold m-1 p-1">
                        Growth Priorities
                      </p>

                      {item.accountGrowthPriorities.map((priority, priorityIndex) => (
                        <div
                          key={priorityIndex}
                          className="rounded m-1 p-1 bg-gray-200"
                        >
                          <p className="text-[10px] font-bold mx-1 px-1">
                            {priority.priorityName}
                          </p>
                          {priority.priorityDesc && (
                            <p className="text-[10px] mx-1 px-1 text-gray-600">
                              {priority.priorityDesc}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {item?.accountRisks?.length > 0 && (
                    <div className="m-1 p-1">
                      <p className="text-xs font-bold m-1 p-1">Risks</p>

                      {item.accountRisks.map((risk, riskIndex) => (
                        <div key={riskIndex} className="rounded m-1 p-1 bg-red-50">
                          <p className="text-[10px] font-bold mx-1 px-1">
                            {risk.riskName}
                          </p>
                          {risk.riskDesc && (
                            <p className="text-[10px] mx-1 px-1 text-gray-600">
                              {risk.riskDesc}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {item?.accountOutro && (
                    <div className="bg-gray-50 rounded text-xs mx-2 px-2 my-1 py-1">
                      <p
                        className="m-1 p-1"
                        dangerouslySetInnerHTML={createMarkup(item.accountOutro)}
                      ></p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const options = { month: "long", year: "numeric" }
  const day = date.getDate()

  const ordinal = n => {
    let s = ["th", "st", "nd", "rd"],
      v = n % 100
    return s[(v - 20) % 10] || s[v] || s[0]
  }

  return (
    <>
      {day}
      <sup>{ordinal(day)}</sup>{" "}
      {new Intl.DateTimeFormat("en-GB", options).format(date)}
    </>
  )
}

export default EtAccountList